import { useTranslation } from "react-i18next"
import { ArrowsLeftRight, CaretDown, ChatCircle, Check, Phone, VideoCamera } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { CommunicationChannel, CommunicationChannelValues } from "@/types/__generated__/union-types"

const CHANNELS = CommunicationChannelValues

const ChannelsFilter = () => {
  const { t } = useTranslation()

  const { filters } = useWaitingRoomFilters()
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)

  const channels = filters.channels || []
  const channelCount = channels.length

  const handleCheckChange = (item: CommunicationChannel) => {
    const index = channels.indexOf(item)
    const newChannels = [...channels]

    if (index < 0) newChannels.push(item)
    else newChannels.splice(index, 1)

    setFilters({ channels: newChannels.length > 0 ? newChannels : null })
  }

  const handleResetChannels = () => {
    setFilters({ channels: null })
  }

  const getChannelIcon = (item: CommunicationChannel, isSelected: boolean) => {
    const iconClass = isSelected ? "text-on-primary-surface mr-2" : "text-on-surface-subdued mr-2"

    switch (item) {
      case "VIDEO":
        return <VideoCamera className={iconClass} />
      case "CHAT":
        return <ChatCircle className={iconClass} />
      case "ASYNC_REQUEST":
        return <ArrowsLeftRight className={iconClass} />
      case "PHONE":
        return <Phone className={iconClass} />
      default:
        return null
    }
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip data-testid="channel-filter-trigger" className="h-8 gap-0.5" checked={channelCount > 0} type="button">
          <div className={`overflow-hidden transition-all duration-200 ${channelCount > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{t("common.channel") + (channelCount > 0 ? ` (${channelCount})` : ``)}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="channel-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <div className="grid gap-0.5">
          {CHANNELS.map((item) => {
            const isSelected = channels.includes(item)
            return (
              <DropdownMenuCheckboxItem
                onSelect={(e) => e.preventDefault()}
                className="h-9"
                key={item}
                checked={isSelected}
                data-testid={`menu-channel-${item.toLowerCase()}`}
                onCheckedChange={() => handleCheckChange(item)}>
                <div className="flex items-center">
                  {getChannelIcon(item, isSelected)}
                  {t(`scheduling.waitingRoom.filters.channelFilter.${item.toLocaleLowerCase()}`)}
                </div>
              </DropdownMenuCheckboxItem>
            )
          })}
        </div>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleResetChannels}
            data-testid="reset-channel-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { ChannelsFilter }
